import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useOnboarding } from "../../../contexts/OnboardingContext";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const GoalsStep = () => {
  const { username, goals, setGoals, nextStep, completeOnboarding, isLastStep, isLoading } = useOnboarding();
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const el = document.getElementById("onboarding-continue-slot");
      if (el) setPortalTarget(el);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const goalOptions = [
    { icon: "📚", label: "Educational",     description: "Turn stories into learning moments with fun facts.",       value: "Educational content" },
    { icon: "🛡️", label: "Safe Content",    description: "Strict safety filters for family-friendly stories.",       value: "Safe content" },
    { icon: "📖", label: "Storybooks",       description: "Beautiful illustrated narratives for bedtime reading.",     value: "Storybooks" },
    { icon: "💛", label: "Emotional Growth", description: "Foster empathy and resilience through thoughtful stories.", value: "Emotional growth" },
    { icon: "🎨", label: "Creative Fun",     description: "Spark imagination with wild adventures and play.",         value: "Creativity" },
    { icon: "🔭", label: "Learn & Explore",  description: "Discover the world through curiosity-driven adventures.",  value: "Learning" },
  ];

  const handleToggle = (value) => {
    if (goals.includes(value)) setGoals(goals.filter((g) => g !== value));
    else setGoals([...goals, value]);
  };

  const continueButton = (
    <button
      onClick={isLastStep ? completeOnboarding : nextStep}
      disabled={isLoading}
      className="inline-flex items-center gap-2 transition active:scale-[0.97] disabled:opacity-60"
      style={{
        ...FREDOKA,
        background: "#0065F8",
        color: "#FFFFFF",
        fontWeight: 700,
        fontSize: 15,
        border: "none",
        borderRadius: 999,
        padding: "12px 22px",
        boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
        cursor: isLoading ? "wait" : "pointer",
      }}
    >
      {isLoading ? (
        <>
          <span style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.30)", borderTopColor: "#FFFFFF", animation: "ob-spin 1s linear infinite" }} />
          Finishing…
        </>
      ) : (
        <>
          Continue
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </>
      )}
    </button>
  );

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col items-center px-4 animate-fadeIn">
      {/* Panda + Speech Bubble */}
      <div className="flex items-start gap-4 sm:gap-5 mb-8 w-full">
        <div className="flex-shrink-0">
          <div className="relative w-24 h-24 sm:w-32 sm:h-32">
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,101,248,0.15)", filter: "blur(28px)", borderRadius: "50%", transform: "scale(1.4)" }} />
            <img
              src="/assets/pixley-panda-nobg.png"
              alt="Pixley Panda"
              className="relative w-full h-full object-contain z-10"
              onError={(e) => { e.target.src = "/assets/pixley-panda.png"; }}
              style={{ filter: "drop-shadow(0px 6px 12px rgba(11,26,51,0.18))" }}
            />
          </div>
        </div>
        <div className="flex-1 relative">
          <div
            className="absolute z-0"
            style={{
              left: -8, top: 28, width: 16, height: 16,
              background: "#FFFFFF",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRight: "none", borderTop: "none",
              transform: "rotate(45deg)",
            }}
          />
          <div
            className="relative z-10 px-6 py-5 sm:px-7 sm:py-6"
            style={{
              background: "#FFFFFF",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRadius: 22,
              boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 24px rgba(11,26,51,0.06)",
            }}
          >
            <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
              Goals
            </span>
            <h1
              style={{
                ...FREDOKA,
                color: "#0B1A33",
                fontWeight: 700,
                fontSize: "clamp(1.3rem, 2.4vw, 1.7rem)",
                textTransform: "uppercase",
                letterSpacing: "-0.01em",
                lineHeight: 1.1,
                margin: "6px 0 6px",
              }}
            >
              Almost there{username ? `, ${username}` : ""}!
            </h1>
            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: 0 }}>
              What would you like to do with Pixley? Pick goals so we can tailor the experience.
            </p>
          </div>
        </div>
      </div>

      {/* Goals Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 w-full mb-4">
        {goalOptions.map((goal) => {
          const isSelected = goals.includes(goal.value);
          return (
            <div
              key={goal.value}
              onClick={() => handleToggle(goal.value)}
              className="group relative cursor-pointer transition-all active:scale-[0.98]"
              style={{
                background: isSelected ? "linear-gradient(180deg, #EAF3FF 0%, #DBEAFE 100%)" : "#FFFFFF",
                border: isSelected ? "3px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                borderRadius: 18,
                padding: "18px",
                boxShadow: isSelected
                  ? "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.20)"
                  : "0px 3px 0px rgba(11,26,51,0.06)",
              }}
            >
              <div
                className="mb-3 flex items-center justify-center"
                style={{
                  width: 40, height: 40,
                  borderRadius: 12,
                  background: isSelected ? "rgba(255,255,255,0.85)" : "rgba(0,101,248,0.08)",
                  fontSize: 22,
                  boxShadow: isSelected ? "0px 2px 0px rgba(11,26,51,0.05)" : "none",
                }}
              >
                {goal.icon}
              </div>

              <h3 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 15, letterSpacing: "-0.01em", margin: "0 0 4px", textTransform: "uppercase" }}>
                {goal.label}
              </h3>
              <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 12, lineHeight: 1.4, margin: 0 }}>
                {goal.description}
              </p>

              <div className="absolute" style={{ top: 12, right: 12 }}>
                {isSelected ? (
                  <div
                    style={{
                      width: 22, height: 22,
                      background: "#0065F8",
                      color: "#FFFFFF",
                      borderRadius: "50%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0px 2px 0px #073B8E",
                    }}
                  >
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <div style={{ width: 22, height: 22, borderRadius: "50%", border: "2px solid rgba(11,26,51,0.15)" }} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p style={{ ...FREDOKA, color: "#94A3B8", fontSize: 12, textAlign: "center", margin: 0 }}>
        {goals.length > 0
          ? `${goals.length} goal${goals.length !== 1 ? "s" : ""} selected · you can change these later in settings.`
          : "You can change these later in settings."}
      </p>

      {portalTarget && ReactDOM.createPortal(continueButton, portalTarget)}

      <style>{`@keyframes ob-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default GoalsStep;
